import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, input, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Subscription, firstValueFrom } from 'rxjs';
import { CustomDatePipe } from 'src/app/pipe/date-format.pipe';
import { tasksServices } from 'src/app/services/tasks.service';
import { DialogComponent } from 'src/app/shared/dialog/confirm.dialog';
import { CardItemComponent } from './card-item/card-item.component';
import { CommunicationService } from '../../signal-lead.serice';
import { ActivatedRoute } from '@angular/router';
import { ActivityCompletionService } from 'src/app/services/activity-completion.service';
import { ActivityModalDataService } from 'src/app/services/lead-activity-modal.service';
import { Subject, takeUntil } from 'rxjs';
import { ActivityLeadService } from 'src/app/services/lead-activity.servise';
import { EventConfirmationDialogComponent } from 'src/app/shared/calendar/event-confirmation-dialog/event-confirmation-dialog.component';
import { userDataService } from 'src/app/services/userDataService';

@Component({
  selector: 'app-activity',
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    CardItemComponent,
    EventConfirmationDialogComponent
  ],
  templateUrl: './activity.component.html',
  styleUrl: './activity.component.scss',
})
export class ActivityComponent implements OnDestroy {
  data = input<any>();
  params = input<any>();
  lead = input<any>();

  hourSegments = 30;
  openActivity: boolean = false;
  private subscription!: Subscription;
  activities: any[] = [];
  total: any;
  showLoad: boolean = true;
  leadId: any;
  private destroy$ = new Subject<void>();
  isModal = input<boolean>(false);

  constructor(
    private taskService: tasksServices,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private CommunicationService: CommunicationService,
    private activityCompletionService: ActivityCompletionService,
    private modalDataService: ActivityModalDataService,
    private activityLeadService: ActivityLeadService,
    private userDataService:userDataService
  ) {
    
    this.leadId = this.route.snapshot.paramMap.get('id') || this.params()?.id;
    effect(() => {
      if(!this.leadId && this.params()?.id){
        this.leadId = this.params()?.id;
      }
    });
  }

  @ViewChild('scrollableContainer') scrollableContainer!: ElementRef;

  expandEditor() {
    this.openActivity = !this.openActivity;
  }

  async ngOnInit() {

    const user = await this.userDataService.getUserData()
    if(user?.Company?.activity_activity_duration){
      this.hourSegments = user?.Company?.activity_activity_duration;
    }

    // Filter to ensure all activities have valid ids for the track expression
    this.activities = (this.data().data || []).filter((item: any) => item?.id);
    this.total = this.data().total;
    if (+this.params().pageIndex * +this.params().pageSize >= this.total) {
      this.showLoad = false;
    }

    this.modalDataService.activityEvents$
    .pipe(takeUntil(this.destroy$))
    .subscribe(async activityEvent => {
      if (!activityEvent) { return; }

      // Handle bulk update (recurring series edit) - reload all activities
      if (activityEvent.action === 'bulk-update') {
        const response = await firstValueFrom(
          this.taskService.getAllActivities(this.params()),
        );
        this.activities = (response.data || []).filter((item: any) => item?.id);
        this.total = response.total;
        return;
      }

      // Handle delete action - filter out the deleted activity
      if (activityEvent.action === 'delete') {
        this.activities = this.activities.filter(row => row.id !== activityEvent.data?.id);
        return;
      }
    
      if(activityEvent.data?.newAppointment || activityEvent.data?.newTask || activityEvent.data?.oldTask){
        // Update old task if exists (oldTask.data contains the actual task)
        const oldTaskData = activityEvent.data?.oldTask?.data || activityEvent.data?.oldTask;
        if (oldTaskData?.id) {
          this.activities = this.activities.map((row) =>
            row.id === oldTaskData.id ? { ...oldTaskData } : row,
          );
        }

        // Add new task only if it has a valid id
        const newTask = activityEvent.data?.newTask;
        if (newTask?.id) {
          this.addActivity(newTask);
        }
      }
     
      else if(activityEvent.action == 'update'){
          this.activities = this.activities.map((row) =>
            row.id === activityEvent.data.id ? { ...activityEvent.data } : row,
      );
      }else{
        this.addActivity(activityEvent.data);
      }
      this.openActivity = false;
    })
  }

  addActivity(result) {
    // Only add if the result has a valid id
    if (result?.id) {
      this.activities.unshift(result);
    }
  }

  // New method to open activity modal when clicking on card
  async openActivityModal(task: any) {
    await this.activityLeadService.openActivityOnlyModal({
      ...task,
      start: new Date(task.startDate),
      end: new Date(task.endDate),
      id: task.id,
      hourSegments: this.hourSegments,
      detailed: true,
      hideCalendarProps: true,
      isExternal: true,
      listView: true,
      defaultLead: this.leadId || task.lead?.id,
      isModal: true,
      hideNavigation: true,
      editMode: true // Since we're editing an existing task
    });


  }

  editActivity(event) {
    // This method is called from the card menu
    this.openActivityModal(event);
  }

  async deleteActivity(id) {
    const header = 'Delete Activity';
    const content = 'Are you sure you want to delete this activity?';
    const confirmed = await this.callDialog(this.data, header, content);

    if (confirmed) {
      const findIndex = this.activities.findIndex((x) => x.id == id);
      const deletedActivity = this.activities[findIndex];

      this.removeActivity(id);

      // Emit delete event through modalDataService to update other activity components
      this.modalDataService.emitEvent('delete', deletedActivity);

      // Also notify through CommunicationService for focus tab and tab caching
      const obj = {
        data: deletedActivity,
        location: 'activity',
        updated: false,
      };
      this.CommunicationService.updateData({
        type: 'focus',
        obj,
      });
    }
  }

  callDialog(event: any, header: string, content: string) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        header,
        content,
      },
      width: '300px', // Optional: customize dialog size
    });

    return dialogRef.afterClosed().toPromise();
  }

  async removeActivity(id) {
    const response = await firstValueFrom(this.taskService.delete(id));
    this.activities = this.activities.filter((row) => row.id != id);
  }


  

  async markCompleted(event: any, skipDbSave = false) {
    console.log(event.lead?.fa_advisor_id,this.lead()?.fa_advisor_id,'asijfhu9afasufasfhsa')
    const currentLead = this.lead();
    // Clone the event to avoid mutating the original
    const activityToUpdate = {
      ...event,
      lead: {
        ...(event.lead || {}),
        enrolled_at: currentLead?.enrolled_at ?? event.lead?.enrolled_at ?? null,
        status: currentLead?.status ?? event.lead?.status,
        isDuplicated: currentLead?.isDuplicated ?? event.lead?.isDuplicated ?? false,
        fa_advisor_id: event.lead?.fa_advisor_id ?? currentLead?.fa_advisor_id ?? null,
      }
    };

    // Store the original state before any changes
    const originalCompletedState = activityToUpdate.completed;


    let response;
    if(!skipDbSave){
      const data:any = await this.activityCompletionService.markActivityAsDone(activityToUpdate);
      if(!data){
        // User canceled, revert the activity to its original state
        this.activities = this.activities.map((row) => {
          if (row.id === event.id) {
            return { ...row, completed: originalCompletedState };
          }
          return row;
        });
        return;
      }

      // Handle reschedule case - the subscription will handle updating the list
      if (data.action === 'rescheduled' || data.newAppointment?.newTask || data.newAppointment?.oldTask) {
        return;
      }

      response = data.data || data;
    } else {
      response = activityToUpdate;
    }

    let indexTask;

    this.activities = this.activities.map((row, i) => {
      if (row.id === event.id) {
        indexTask = i;
        return { ...row, ...response };
      }
      return row;
    });
    if (response) {
      // Notify through CommunicationService for focus tab and tab caching.
      // The 'update' on modalDataService.activityEvents$ is already emitted by
      // ActivityCompletionService.markActivityAsDone — emitting again here
      // double-fires every subscriber.
      const obj = {
        data: this.activities[indexTask],
        location: 'activity',
        updated: false,
        updateActivityTime: this.activities[indexTask]?.completed
      };
      this.CommunicationService.updateData({
        type: 'focus',
        obj,
      });
    }
  }

  async moreData() {
    this.params().pageIndex++;
    if (+this.params().pageIndex * +this.params().pageSize >= this.total) {
      this.showLoad = false;
    }
    const response = await firstValueFrom(
      this.taskService.getAllActivities(this.params()),
    );
    // Filter to ensure all new activities have valid ids
    const validActivities = (response.data || []).filter((item: any) => item?.id);
    this.activities.push(...validActivities);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}